import { useState, type MouseEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PROMO_RATE } from '@klaro7/shared-types';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAvailability } from '@/features/calls/hooks/useAvailability';
import { useCall } from '@/features/calls/context/CallContext';
import { FavoriteButton } from '@/features/favorites/components/FavoriteButton';
import { useMyAvailabilityWatches } from '@/features/availability-watch/hooks/useAvailabilityWatch';
import { NotifyWhenOnlineButton } from '@/features/availability-watch/components/NotifyWhenOnlineButton';
import { resolveUploadUrl } from '@/lib/uploads';
import { professionalStatusDisplay } from '@/features/professionals/lib/statusDisplay';
import type { PublicProfessional } from '../services/professional-public.service';
import { StarRating } from './StarRating';

interface FeaturedCardProps {
  professional: PublicProfessional;
  rank: number;
  showPromoPrice?: boolean;
  variant?: 'full' | 'compact';
  /** Pin the card to a fixed pixel height; overflow scrolls inside. */
  height?: number;
}

const RANK_COLORS: Record<number, string> = {
  1: 'from-brand-orange to-brand-orange-600',
  2: 'from-slate-500 to-slate-600',
  3: 'from-brand-orange-600 to-brand-orange-dark',
  4: 'from-brand-teal-400 to-brand-teal-600',
  5: 'from-brand-teal-500 to-brand-teal-700',
  6: 'from-brand-navy/70 to-brand-navy',
  7: 'from-neutral-500 to-neutral-700',
};

export function FeaturedCard({ professional, rank, showPromoPrice, variant = 'full', height }: FeaturedCardProps) {
  const isCompact = variant === 'compact';
  const navigate = useNavigate();
  const profileHref = `/consultant/${professional.id}`;

  const handleCardActivate = () => {
    navigate(profileHref);
  };

  const handleCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardActivate();
    }
  };

  // Stop bubbling so clicking on inner interactive controls (favorite,
  // call, notify) doesn't double-trigger a navigation to the profile.
  const stop = (e: MouseEvent) => e.stopPropagation();
  const { isAuthenticated } = useAuth();

  // Paid chat: logged-out users have no call socket, so initiateCall would flip
  // the overlay into a non-idle chat state with no way back. Gate behind auth.
  const handleChat = (e: MouseEvent) => {
    stop(e);
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(profileHref)}`);
      return;
    }
    initiateCall(professional.id, 'chat', fullName);
  };
  const { getStatus } = useAvailability();
  const { initiateCall } = useCall();
  const { data: watchData } = useMyAvailabilityWatches(isAuthenticated);
  const isWatching = !!watchData?.watches?.some((w) => w.professionalId === professional.id);
  const [imgError, setImgError] = useState(false);
  const user = professional.userId;
  const fullName = `${user?.profile?.firstName ?? ''} ${user?.profile?.lastName ?? ''}`.trim();
  const onlineStatus = getStatus(professional.id, professional.onlineStatus);
  const isAvailable = onlineStatus === 'online';
  const status = professionalStatusDisplay(onlineStatus);
  const callEnabled =
    professional.serviceChannels?.audioCall !== false || professional.serviceChannels?.videoCall !== false;
  const chatEnabled = professional.serviceChannels?.chat !== false;

  // Defensive: a professional whose populated user relation is missing
  // (deleted/orphaned account) would otherwise crash the featured grid.
  if (!user?.profile) return null;

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={handleCardActivate}
      onKeyDown={handleCardKeyDown}
      aria-label={`Apri profilo di ${fullName}`}
      style={height != null ? { height } : undefined}
      className={`group relative cursor-pointer overflow-hidden rounded-xl border backdrop-blur-sm transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange/40 focus-visible:ring-offset-2 ${
        isCompact
          ? 'border-neutral-200 bg-white hover:-translate-y-1 hover:border-brand-teal/40 hover:shadow-[0_10px_30px_-12px_rgba(24,153,170,0.25)]'
          : 'border-brand-orange/25 bg-gradient-to-br from-brand-orange/[0.06] to-transparent hover:border-brand-orange/40 hover:shadow-[0_0_30px_rgba(235,114,35,0.1)] hover:scale-[1.01]'
      }`}
    >
      {/* Rank badge */}
      <div
        className={`absolute left-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br ${RANK_COLORS[rank] ?? RANK_COLORS[3]} text-xs font-bold text-white`}
      >
        {rank}
      </div>

      <div className={`flex h-full flex-col overflow-y-auto text-center ${isCompact ? 'px-4 pb-4 pt-9' : 'px-5 pb-5 pt-12'}`}>
        {/* Avatar + status */}
        <div className={`relative mx-auto w-fit ${isCompact ? 'mb-2' : 'mb-3'}`}>
          {user.profile?.avatar && !imgError ? (
            <img
              src={resolveUploadUrl(user.profile.avatar)}
              alt={fullName}
              className={`rounded-full border-2 object-cover ${
                isCompact
                  ? 'h-16 w-16 border-brand-teal/30'
                  : 'h-24 w-24 border-brand-orange/30 shadow-[0_0_20px_rgba(235,114,35,0.15)]'
              }`}
              onError={() => setImgError(true)}
            />
          ) : (
            <div
              className={`flex items-center justify-center rounded-full border-2 bg-neutral-100 ${
                isCompact
                  ? 'h-16 w-16 border-brand-teal/30'
                  : 'h-24 w-24 border-brand-orange/30 shadow-[0_0_20px_rgba(235,114,35,0.15)]'
              }`}
            >
              <img
                src="/images/logo-navy.png"
                alt=""
                className={`object-contain opacity-60 ${isCompact ? 'h-9 w-9' : 'h-14 w-14'}`}
              />
            </div>
          )}
          <span className="absolute bottom-1 right-1" title={status.label}>
            <span className={`block h-4 w-4 rounded-full border-[2.5px] border-white ${status.dotClass}`} />
            {status.pulse && (
              <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400/40" />
            )}
          </span>
        </div>

        <div className="mb-0.5 flex items-center justify-center gap-2">
          <h3 className={`font-bold text-brand-navy ${isCompact ? 'text-base' : 'text-lg'}`}>{fullName}</h3>
          <span onClick={stop}>
            <FavoriteButton professionalId={professional.id} size="sm" />
          </span>
        </div>

        {professional.title && (
          <p className="mb-2 text-sm text-brand-orange/50 line-clamp-1">{professional.title}</p>
        )}

        {/* Specializations — single fixed-height line, zero clipping.
            ONE primary chip absorbs all width pressure (min-w-0 truncate, no fixed
            width, no shrink-0) so it ellipsizes inside itself; the +N pill is
            shrink-0 and tiny, so the row sum can never exceed the card inner width. */}
        {(() => {
          const [primary, ...rest] = professional.specializations;
          const extra = rest.length;
          return (
            <div className={`flex h-[22px] w-full items-center justify-center gap-1 ${isCompact ? 'mb-2' : 'mb-3'}`}>
              {primary && (
                <span
                  title={primary}
                  aria-label={primary}
                  className="min-w-0 truncate rounded-full bg-brand-teal/15 px-2.5 py-0.5 text-[11px] font-medium leading-[16px] text-brand-teal-400"
                >
                  {primary}
                </span>
              )}
              {extra > 0 && (
                <span
                  title={rest.join(', ')}
                  aria-label={`Altre ${extra} specializzazioni: ${rest.join(', ')}`}
                  className="shrink-0 rounded-full bg-brand-teal/10 px-2 py-0.5 text-[11px] font-medium leading-[16px] text-brand-teal-400/80"
                >
                  +{extra}
                </span>
              )}
            </div>
          );
        })()}

        {/* Rating */}
        <div className="mb-3 flex justify-center">
          <StarRating rating={professional.rating ?? 0} reviewCount={professional.reviewCount} size="md" />
        </div>

        {/* Price */}
        {professional.costPerMinute != null && (
          <p className="mb-4 text-neutral-500">
            {showPromoPrice ? (
              <>
                <span className="text-sm font-medium text-neutral-400 line-through">{professional.costPerMinute.toFixed(2)}&euro;</span>{' '}
                <span className="text-xl font-bold text-brand-orange">{PROMO_RATE.toFixed(2).replace('.', ',')}&euro;</span>
                <span className="text-sm"> /min</span>
              </>
            ) : (
              <>
                <span className="text-xl font-bold text-brand-orange">
                  {professional.costPerMinute.toFixed(2)}
                </span>
                <span className="text-sm"> &euro;/min</span>
              </>
            )}
          </p>
        )}

        {/* CTA Buttons — online: Chiama + Chat (paid chat session).
            offline/busy: only "Avvisami".
            Fixed-height block keeps the card layout stable across states. */}
        <div className="flex min-h-[148px] flex-col justify-end gap-2" onClick={stop}>
          {isAvailable ? (
            <>
              {callEnabled && (
                <Link
                  to={profileHref}
                  onClick={stop}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white shadow-[0_0_20px_rgba(235,114,35,0.25)] transition-all duration-200 hover:bg-brand-orange-600 min-h-[44px]"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                  Chiama
                </Link>
              )}
              {chatEnabled && (
                <button
                  type="button"
                  onClick={handleChat}
                  className="inline-flex w-full min-h-[44px] items-center justify-center gap-2 rounded-lg border border-brand-teal/30 bg-brand-teal/10 px-5 py-2.5 text-sm font-semibold text-brand-teal transition-all duration-200 hover:bg-brand-teal/20 hover:border-brand-teal/50"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Chat
                </button>
              )}
            </>
          ) : (
            <NotifyWhenOnlineButton
              professionalId={professional.id}
              professionalName={fullName}
              isWatching={isWatching}
              className="w-full justify-center"
            />
          )}
        </div>
      </div>
    </div>
  );
}
